import { useState, useSyncExternalStore } from 'react';
import type { RoutablePageConfig } from '@kizenapps/engine';

export type RegisterInlinePages = (
  pluginApiName: string,
  pages: RoutablePageConfig[]
) => () => void;

type Registration = { token: object; pages: RoutablePageConfig[] };

export type InlinePagesRegistry = {
  register: RegisterInlinePages;
  getSnapshot: () => RoutablePageConfig[];
  subscribe: (listener: () => void) => () => void;
};

export const createInlinePagesRegistry = (): InlinePagesRegistry => {
  const registrations = new Map<string, Registration[]>();
  const listeners = new Set<() => void>();
  let snapshot: RoutablePageConfig[] = [];

  const emit = () => {
    snapshot = [...registrations.values()].flatMap(
      (stack) => stack[stack.length - 1]?.pages ?? []
    );
    listeners.forEach((listener) => listener());
  };

  const register: RegisterInlinePages = (pluginApiName, pages) => {
    const registration: Registration = { token: {}, pages };
    registrations.set(pluginApiName, [
      ...(registrations.get(pluginApiName) ?? []),
      registration,
    ]);
    emit();

    let released = false;
    return () => {
      if (released) return;
      released = true;
      const remaining = (registrations.get(pluginApiName) ?? []).filter(
        (entry) => entry.token !== registration.token
      );
      if (remaining.length) {
        registrations.set(pluginApiName, remaining);
      } else {
        registrations.delete(pluginApiName);
      }
      emit();
    };
  };

  return {
    register,
    getSnapshot: () => snapshot,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
};

export const withInlinePages = (
  routablePages: RoutablePageConfig[],
  inlinePages: RoutablePageConfig[]
) => (inlinePages.length ? [...routablePages, ...inlinePages] : routablePages);

export const useInlinePagesRegistry = () => {
  const [registry] = useState(createInlinePagesRegistry);
  return registry;
};

export const useInlinePages = (registry: InlinePagesRegistry) =>
  useSyncExternalStore(registry.subscribe, registry.getSnapshot);
